import { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { LIST_TICKETS, LIST_TICKET_CATEGORIAS } from './gql.js';

const ESTADOS = [
  { value: 'NUEVO', label: 'Nuevo' },
  { value: 'EN_PROGRESO', label: 'En progreso' },
  { value: 'PENDIENTE_CLIENTE', label: 'Pendiente cliente' },
  { value: 'RESUELTO', label: 'Resuelto' },
  { value: 'CERRADO', label: 'Cerrado' },
  { value: 'ANULADO', label: 'Anulado' },
];

const PRIORIDADES = [
  { value: 'BAJA', label: 'Baja' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'ALTA', label: 'Alta' },
  { value: 'CRITICA', label: 'Crítica' },
];

export default function TicketsList({ onCreate, onSelectTicket }) {
  const [filters, setFilters] = useState({ estado: '', prioridad: '', categoriaId: '', search: '', desde: '', hasta: '' });
  const [pagination, setPagination] = useState({ limit: 20, offset: 0 });

  const [{ data: categoriasData }] = useQuery({ query: LIST_TICKET_CATEGORIAS });
  const [result] = useQuery({
    query: LIST_TICKETS,
    variables: {
      pagination,
      filters: {
        estado: filters.estado || null,
        prioridad: filters.prioridad || null,
        categoriaId: filters.categoriaId || null,
        search: filters.search || null,
        desde: filters.desde || null,
        hasta: filters.hasta || null,
      },
    },
  });

  const categorias = useMemo(() => categoriasData?.listTicketCategorias ?? [], [categoriasData]);
  const items = result.data?.listTickets.items ?? [];
  const pageInfo = result.data?.listTickets.pageInfo ?? pagination;

  const handleSubmit = (event) => {
    event.preventDefault();
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  return (
    <div className="w-full">
      <PageHeader
        title="Tickets"
        subtitle="Gestión integral de reclamos vinculados a envíos"
        actions={(
          <button
            type="button"
            onClick={onCreate}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Crear ticket
          </button>
        )}
      />

      <section className="mx-auto mt-6 w-full max-w-[1400px] rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
        <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Buscar" htmlFor="search">
              <input
              id="search"
              name="search"
              value={filters.search}
              onChange={event => setFilters(prev => ({ ...prev, search: event.target.value }))}
              placeholder="Buscar por asunto, descripción o ID"
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <Field label="Estado" htmlFor="estado">
              <select
              id="estado"
              value={filters.estado}
              onChange={event => setFilters(prev => ({ ...prev, estado: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="">Todos</option>
              {ESTADOS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <Field label="Prioridad" htmlFor="prioridad">
              <select
              id="prioridad"
              value={filters.prioridad}
              onChange={event => setFilters(prev => ({ ...prev, prioridad: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="">Todas</option>
              {PRIORIDADES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <Field label="Categoría" htmlFor="categoria">
              <select
              id="categoria"
              value={filters.categoriaId}
              onChange={event => setFilters(prev => ({ ...prev, categoriaId: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              <option value="">Todas</option>
              {categorias.map(categoria => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                </option>
              ))}
              </select>
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <Field label="Desde" htmlFor="desde">
              <input
              id="desde"
              type="date"
              value={filters.desde}
              onChange={event => setFilters(prev => ({ ...prev, desde: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 sm:col-span-6 md:col-span-3">
            <Field label="Hasta" htmlFor="hasta">
              <input
              id="hasta"
              type="date"
              value={filters.hasta}
              onChange={event => setFilters(prev => ({ ...prev, hasta: event.target.value }))}
              className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          </div>
          <div className="col-span-12 flex items-end justify-end">
            <button
              type="submit"
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Aplicar filtros
            </button>
          </div>
        </form>

        <div className="mt-8 overflow-x-auto">
          <table className="min-w-full table-auto border-separate border-spacing-y-2">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Asunto</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2">Prioridad</th>
                <th className="px-3 py-2">Categoría</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Creado</th>
              </tr>
            </thead>
            <tbody>
              {result.fetching ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                    Cargando tickets...
                  </td>
                </tr>
              ) : null}
              {!result.fetching && items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6">
                    <EmptyState
                      title="Sin tickets"
                      description="No encontramos tickets que coincidan con los filtros seleccionados. Ajustá los filtros o creá un nuevo reclamo."
                    />
                  </td>
                </tr>
              ) : null}
              {items.map(ticket => (
                <tr
                  key={ticket.id}
                  className="cursor-pointer rounded-xl bg-[rgba(var(--subpanel),0.7)] text-sm text-[rgb(var(--fg))] shadow transition hover:bg-[rgba(var(--subpanel),0.9)]"
                  onClick={() => onSelectTicket?.(ticket)}
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectTicket?.(ticket);
                    }
                  }}
                >
                  <td className="px-3 py-3 font-semibold">#{ticket.id}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col">
                      <span className="font-semibold text-[rgb(var(--fg))]">{ticket.asunto}</span>
                      <span className="text-xs text-[rgba(var(--muted),0.9)]">{ticket.canal}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge value={ticket.estado} />
                  </td>
                  <td className="px-3 py-3">
                    <PriorityBadge value={ticket.prioridad} />
                  </td>
                  <td className="px-3 py-3 text-sm">{ticket.categoria?.nombre ?? '—'}</td>
                  <td className="px-3 py-3 text-sm">Cliente #{ticket.clienteId}</td>
                  <td className="px-3 py-3 text-sm">{new Date(ticket.creadoEn).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <StickyActions>
        <div className="flex w-full items-center justify-between gap-4">
          <p className="text-xs text-[rgba(var(--muted),0.9)]">
            Mostrando {items.length} de {pageInfo.total} tickets
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setPagination(prev => ({ ...prev, offset: Math.max(prev.offset - prev.limit, 0) }))
              }
              disabled={pageInfo.offset <= 0}
              className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1 text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() =>
                setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))
              }
              disabled={pageInfo.offset + pageInfo.limit >= pageInfo.total}
              className="rounded-xl border border-[rgba(var(--border),0.6)] px-3 py-1 text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              Siguiente
            </button>
          </div>
        </div>
      </StickyActions>
    </div>
  );
}

function StatusBadge({ value }) {
  const palettes = {
    NUEVO: 'bg-emerald-500/20 text-emerald-500',
    EN_PROGRESO: 'bg-sky-500/20 text-sky-500',
    PENDIENTE_CLIENTE: 'bg-amber-500/20 text-amber-500',
    RESUELTO: 'bg-lime-500/20 text-lime-500',
    CERRADO: 'bg-slate-500/20 text-slate-500',
    ANULADO: 'bg-rose-500/20 text-rose-500',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${palettes[value] ?? 'bg-slate-500/20 text-slate-500'}`}>
      {value?.replaceAll('_', ' ')}
    </span>
  );
}

function PriorityBadge({ value }) {
  const palettes = {
    BAJA: 'bg-slate-500/20 text-slate-500',
    MEDIA: 'bg-sky-500/20 text-sky-500',
    ALTA: 'bg-amber-500/20 text-amber-500',
    CRITICA: 'bg-rose-500/20 text-rose-500',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${palettes[value] ?? 'bg-slate-500/20 text-slate-500'}`}>
      {value}
    </span>
  );
}
